import Layout from '@components/layout/layout-six'
import Seo from '@components/seo/seo'
import Container from '@components/ui/container'
import Divider from '@components/ui/divider'
import Image from '@components/ui/image'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import {
  InstantSearch,
  SearchBox,
  CurrentRefinements,
  ClearRefinements,
  Configure,
  InfiniteHits,
} from 'react-instantsearch-dom'
import { SearchFacets } from '@components/search/search-facets'
import { SearchSort } from '@components/search/search-sort'
import { Element } from 'react-scroll'
import { PLPSearchHit } from '@components/common/plp-search-hit'
import { useTranslation } from 'next-i18next'
import styles from '@components/search/search.module.scss'
import {
  deduplicateRefinement,
  typesenseInstantsearchAdapter,
  typesenseInstantsearchAdapterNumber,
} from '@utils/helper'
import { SearchStats } from '@components/search/search-stats'
import BenefitsBar from '@components/common/benefits-bar'
import { SearchMobileFacets } from '@components/search/search-mobile-facets'

export default function Search({ locale, indexName }: any) {
  const { query } = useRouter()
  const { t: translate } = useTranslation('common')

  const deduplicate = (items: any) => {
    return deduplicateRefinement(items, translate)
  }

  // Let's determine which page we're on since this search page also handles other pages
  let pageTitle = translate('text-search')
  let q = query.q
  let path = '/search'
  let filter = 'is_active: true && search_visible:!= false && is_in_stock: true'
  if (query.page) {
    if (query.page === 'direct_deals') {
      pageTitle = translate('text-direct-deals')
      path = '/deals'
      q = '*'
      filter += ' && is_direct_deal:true'
    }
    if (query.page === 'liquidation') {
      pageTitle = translate('text-liquidation-deals')
      path = '/clearance.html'
      q = '*'
      filter += ' && is_liquidation:true'
    }
    if (query.page === 'new_arrivals') {
      pageTitle = translate('text-new-arrivals')
      path = '/new-arrivals.html'
      q = '*'
      filter += ' && is_new_arrival:true'
    }
  }

  return (
    <>
      <InstantSearch
        indexName={indexName}
        searchClient={
          !isNaN(Number(q))
            ? typesenseInstantsearchAdapterNumber.searchClient
            : typesenseInstantsearchAdapter.searchClient
        }
      >
        <Configure filters={filter} hitsPerPage={40} />
        <Seo
          title={pageTitle}
          description={translate('text-home-seo-blurb', { ns: 'common' })}
          path={path}
        />
        <Divider />
        <Container>
          {query.page && (
            <Image
              width={0}
              height={0}
              sizes="100vw"
              src={`/assets/images/hero/${query.page}_${locale}.png`}
              style={{ width: '100%', height: 'auto' }}
              alt={pageTitle}
            />
          )}
          {/* @ts-ignore */}
          <Element name="grid" className="pb-16 pt-7 lg:pt-7 lg:pb-20">
            <SearchBox defaultRefinement={String(q)} className="hidden" />
            <div>
              <div className={styles.refinementArea}>
                {query.page && (
                  <>
                    <h1 className={styles.title}>{pageTitle}</h1>
                    <div className={styles.blurb}>
                      {translate(
                        `text-${String(query.page).replace('_', '-')}-blurb`,
                        { ns: 'common' },
                      )}
                    </div>
                  </>
                )}
                <CurrentRefinements
                  classNames={styles.refinementList}
                  transformItems={deduplicate}
                ></CurrentRefinements>
                <ClearRefinements></ClearRefinements>
                <div className="relative float-right whitespace-nowrap">
                  <SearchMobileFacets
                    facets={<SearchFacets locale={locale} />}
                  />
                </div>
                <SearchSort indexName={indexName} locale={locale}></SearchSort>
              </div>
              <div className="pl-3">
                {!query.page && (
                  <h1 className={`${styles.searchTitle} mb-2`}>
                    {translate('text-results_for')}: &quot;{q}&quot;
                  </h1>
                )}
                <SearchStats searchName={''} />
              </div>
              <div className="search_results flex">
                <SearchFacets locale={locale} />
                <div className={styles.searchResultsHits}>
                  {query.page && (
                    <div>
                      <SearchBox
                        className={styles.searchBox}
                        translations={{
                          placeholder: translate('text-search-within'),
                        }}
                      />
                    </div>
                  )}
                  <div className="w-full">
                    {/* @ts-ignore */}
                    <InfiniteHits hitComponent={PLPSearchHit} />
                    {/* @ts-ignore */}
                  </div>
                </div>
              </div>
            </div>
          </Element>
        </Container>
        <div className="w-full mt-24 lg:mt-0--">
          <BenefitsBar />
        </div>
        <Divider />
      </InstantSearch>
    </>
  )
}

Search.Layout = Layout

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const indexName =
    process.env[`NEXT_PUBLIC_TS_${locale.replace('-', '_').toUpperCase()}_REPO`]

  return {
    props: {
      locale,
      indexName,
      ...(await serverSideTranslations(locale!, [
        'common',
        'categories',
        'forms',
        'home',
        'sort',
        'menu',
        'footer',
      ])),
    },
    revalidate: 60,
  }
}
